import { useSyncExternalStore } from "react";
/**
 * @typedef {Object} EngineStats
 * @property {number} frameCount       Monotonic frame counter since the engine started emitting.
 * @property {number} measuredFps      Rolling-1s frames-per-second sample.
 * @property {Object|null} source      Currently attached source descriptor, or null.
 * @property {Array<Object>} activeLenses  Currently loaded lenses in stack order.
 * @property {number|null} coreVersion The LensCore wasm version, once known.
 */
const INITIAL_STATS = {
    frameCount: 0,
    measuredFps: 0,
    source: null,
    activeLenses: [],
    coreVersion: null,
};
/**
 * Creates an external, mutable store that the engine writes to on every frame
 * and that React consumers subscribe to via {@link useFrameStats}.
 *
 * Designed so that engine state changes do NOT cause the {@link EngineProvider}
 * to re-render its context value. Consumers pick the slice they care about
 * with a selector and only re-render when that slice changes.
 */
export function createEngineStatsStore() {
    let state = INITIAL_STATS;
    const listeners = new Set();
    let fpsLastSampleAt = 0;
    let fpsFramesSinceSample = 0;
    let pendingFrameCount = 0;
    function set(partial) {
        state = { ...state, ...partial };
        for (const listener of listeners) {
            try {
                listener();
            }
            catch { /* state observers must never break LensCore's frame callback */ }
        }
    }
    return {
        get() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        recordFrame(count) {
            // Throttled to 1Hz so subscribers don't re-render on every frame.
            // frameCount, when displayed, may lag by up to ~1s — fine for a human-readable counter.
            const now = performance.now();
            fpsFramesSinceSample += 1;
            pendingFrameCount = Number(count) || pendingFrameCount + 1;
            if (fpsLastSampleAt === 0) {
                fpsLastSampleAt = now;
                return;
            }
            const elapsed = now - fpsLastSampleAt;
            if (elapsed < 1000)
                return;
            const nextFps = (fpsFramesSinceSample * 1000) / elapsed;
            fpsLastSampleAt = now;
            fpsFramesSinceSample = 0;
            set({ frameCount: pendingFrameCount, measuredFps: nextFps });
        },
        setSource(source) {
            set({ source });
        },
        setActiveLenses(activeLenses) {
            set({ activeLenses });
        },
        setCoreVersion(coreVersion) {
            set({ coreVersion });
        },
        reset() {
            state = INITIAL_STATS;
            fpsLastSampleAt = 0;
            fpsFramesSinceSample = 0;
            for (const listener of listeners) {
                try {
                    listener();
                }
                catch { /* state observers must never break engine reset */ }
            }
        },
    };
}
const identitySelector = (state) => state;
/**
 * Subscribes the calling component to a slice of the engine stats store.
 * Re-renders only when the selected value changes (Object.is equality).
 *
 * @template T
 * @param {ReturnType<typeof createEngineStatsStore>} store
 * @param {(stats: EngineStats) => T} [selector] - Defaults to the whole stats object.
 * @returns {T}
 */
export function useFrameStatsFromStore(store, selector = identitySelector) {
    return useSyncExternalStore(store.subscribe, () => selector(store.get()), () => selector(INITIAL_STATS));
}
//# sourceMappingURL=engineStats.js.map